/** Número da OS: ano + sequência de 5 dígitos (ex.: 202600001), como no protótipo. */
export function formatOrderNumber(year: number, sequence: number): string {
  return `${year}${String(sequence).padStart(5, '0')}`;
}

/**
 * Converte os formatos antigos do protótipo para o formato atual (`compactOrderId`):
 * "OS-2026-12" → "202600012"; "OS-1048" → "<ano>01048".
 */
export function compactLegacyOrderNumber(id: string, fallbackYear: number): string {
  const value = String(id ?? '').trim();
  let m = value.match(/^OS-(\d{4})-(\d+)$/i);
  if (m) return m[1] + m[2].padStart(5, '0');
  m = value.match(/^OS-(\d+)$/i);
  if (m) return String(fallbackYear) + m[1].padStart(5, '0');
  return value;
}

export function parseOrderNumber(number: string): { year: number; sequence: number } | null {
  const m = String(number).match(/^(\d{4})(\d{5,})$/);
  return m ? { year: Number(m[1]), sequence: Number(m[2]) } : null;
}

/** Tipo de atendimento da OS. Em garantia e Retorno exigem OS de origem do mesmo cliente. */
export const SERVICE_TYPES = ['NORMAL', 'AUTORIZADA', 'EM_GARANTIA', 'RETORNO'] as const;
export type ServiceType = (typeof SERVICE_TYPES)[number];
export const SERVICE_TYPE_LABELS: Record<ServiceType, string> = {
  NORMAL: 'Normal',
  AUTORIZADA: 'Autorizada',
  EM_GARANTIA: 'Em garantia',
  RETORNO: 'Retorno',
};
/** Tipos que precisam apontar para uma OS anterior. */
export const serviceTypeNeedsOrigin = (t: ServiceType | null | undefined) => t === 'EM_GARANTIA' || t === 'RETORNO';

/** Backup: null = não informado. */
export const BACKUP_STATUSES = ['PENDING', 'DONE', 'DECLINED'] as const;
export type BackupStatus = (typeof BACKUP_STATUSES)[number];
export const BACKUP_STATUS_LABELS: Record<BackupStatus, string> = {
  PENDING: 'Pendente',
  DONE: 'Feito',
  DECLINED: 'Cliente dispensou',
};

export const BACKUP_ITEM_KINDS = ['PHOTOS', 'CONTACTS', 'WHATSAPP', 'DOCUMENTS', 'APP_DATA', 'OTHER'] as const;
export type BackupItemKind = (typeof BACKUP_ITEM_KINDS)[number];
export const BACKUP_ITEM_LABELS: Record<BackupItemKind, string> = {
  PHOTOS: 'Fotos e vídeos',
  CONTACTS: 'Contatos',
  WHATSAPP: 'Conversas do WhatsApp',
  DOCUMENTS: 'Documentos e arquivos',
  APP_DATA: 'Dados de aplicativos',
  OTHER: 'Outro',
};
/** Item de backup. A posição na lista é a prioridade (primeiro = mais importante). */
export interface BackupItem {
  kind: BackupItemKind;
  /** Obrigatório para OTHER; ignorado nos demais. */
  label?: string | null;
}
export const backupItemLabel = (i: BackupItem) => (i.kind === 'OTHER' ? i.label || 'Outro' : BACKUP_ITEM_LABELS[i.kind]);

/** Último dia de garantia de um item: data de entrega/conclusão + dias. null = sem garantia ou sem data. */
export function warrantyEndDate(from: string | Date | null | undefined, days: number): Date | null {
  if (!from || !days || days <= 0) return null;
  const d = new Date(from);
  if (Number.isNaN(d.getTime())) return null;
  return new Date(d.getTime() + days * 24 * 3600_000);
}
